import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import FolderIcon from '@mui/icons-material/Folder';
import { API_BASE } from '../../shared/Constants';

/** Preset emoji icons and colors a user can pick for a project (feature E). */
export const PROJECT_ICONS = ['📁', '⚖️', '🚀', '📚', '💡', '📊', '🧾', '🔬', '💼', '🏛️', '📝', '❤️'];
export const PROJECT_COLORS = ['#6b7280', '#e11d48', '#ea580c', '#d97706', '#059669', '#0891b2', '#2563eb', '#7c3aed', '#db2777'];

const MAX_INSTRUCTIONS = 4000;

/**
 * Renders a project's chosen emoji (tinted with its color) or, when none is set,
 * the default folder icon — optionally tinted with the chosen color.
 */
export const ProjectIcon: React.FC<{ icon?: string; color?: string; size?: number }> = ({ icon, color, size = 20 }) => {
  if (icon) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size * 1.5,
          height: size * 1.5,
          borderRadius: 8,
          background: color ? color + '22' : 'transparent',
          fontSize: size,
          lineHeight: 1,
        }}
      >
        {icon}
      </span>
    );
  }
  return <FolderIcon style={{ color: color || undefined, fontSize: size * 1.25 }} />;
};

// ---------------------------------------------------------------------------
// Settings panel: icon + color + per-project instructions (features D & E)
// ---------------------------------------------------------------------------

const Panel = styled.div`
  margin: 12px 0 20px;
  border: 1px solid ${({ theme }) => theme.user_menu_border || theme.sidebar_border};
  border-radius: 10px;
  padding: 10px 14px;
`;

const Toggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  font-size: 0.9rem;
  font-weight: 600;
  padding: 4px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin: 14px 0 6px;
  color: ${({ theme }) => theme.font_color};
`;

const Hint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 6px;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const IconChoice = styled.button<{ $sel: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 2px solid ${({ $sel, theme }) => ($sel ? theme.font_color : 'transparent')};
  background: rgba(128, 128, 128, 0.12);
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.font_color};
`;

const Swatch = styled.button<{ $sel: boolean; $c: string }>`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${({ $c }) => $c};
  border: 3px solid ${({ $sel, theme }) => ($sel ? theme.font_color : 'transparent')};
  cursor: pointer;
`;

const InstrArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 90px;
  resize: vertical;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.user_menu_border || theme.sidebar_border};
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-family: inherit;
  &:focus { border-color: ${({ theme }) => theme.font_color}; outline: none; }
`;

const SaveBtn = styled.button`
  margin-top: 10px;
  padding: 7px 16px;
  border-radius: 8px;
  border: none;
  background: ${({ theme }) => theme.font_color};
  color: ${({ theme }) => theme.sidebar_background_solid || theme.sidebar_background};
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: default; }
`;

export const ProjectSettingsPanel: React.FC<{ project: any; onSaved: (p: any) => void }> = ({ project, onSaved }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [open, setOpen] = useState(false);
  const [icon, setIcon] = useState<string>(project.icon || '');
  const [color, setColor] = useState<string>(project.color || '');
  const [instructions, setInstructions] = useState<string>(project.instructions || '');
  const [saving, setSaving] = useState(false);

  // Re-sync the draft when the user switches to a different project.
  useEffect(() => {
    setIcon(project.icon || '');
    setColor(project.color || '');
    setInstructions(project.instructions || '');
  }, [project.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty =
    (icon || '') !== (project.icon || '') ||
    (color || '') !== (project.color || '') ||
    (instructions || '') !== (project.instructions || '');

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/projects/update`, {
        method: 'POST',
        headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: project.project_id, icon, color, instructions }),
      });
      onSaved({ ...project, icon, color, instructions });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel>
      <Toggle onClick={() => setOpen(o => !o)} data-testid="project-settings-toggle">
        {open ? '▾' : '▸'} Personalizar projeto
      </Toggle>
      {open && (
        <div>
          <Label>Ícone</Label>
          <Row>
            <IconChoice $sel={!icon} onClick={() => setIcon('')} title="Padrão">
              <FolderIcon style={{ fontSize: 18 }} />
            </IconChoice>
            {PROJECT_ICONS.map(e => (
              <IconChoice key={e} $sel={icon === e} onClick={() => setIcon(e)}>{e}</IconChoice>
            ))}
          </Row>

          <Label>Cor</Label>
          <Row>
            {PROJECT_COLORS.map(c => (
              <Swatch key={c} $c={c} $sel={color === c} onClick={() => setColor(color === c ? '' : c)} aria-label={`cor ${c}`} />
            ))}
          </Row>

          <Label>Instruções do projeto</Label>
          <Hint>Aplicadas a todas as conversas deste projeto.</Hint>
          <InstrArea
            data-testid="project-instructions-input"
            value={instructions}
            maxLength={MAX_INSTRUCTIONS}
            placeholder="Ex.: Foque em direito tributário e cite sempre a legislação."
            onChange={e => setInstructions(e.target.value.slice(0, MAX_INSTRUCTIONS))}
          />

          <SaveBtn data-testid="project-settings-save" disabled={!dirty || saving} onClick={handleSave}>
            {saving ? 'Salvando...' : 'Salvar'}
          </SaveBtn>
        </div>
      )}
    </Panel>
  );
};
